import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';
import {
  IconButton,
  IIconProps,
  Label,
  MessageBarType,
  PrimaryButton,
  Separator,
  Spinner,
  Text
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';

// models
import { ExternalEntityAssociationViewModel } from '../../../models/Entities/Person/ExternalAssociation/ExternalAssociationModels';

// components
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import PersonExternalAssociationFormComponent from './PersonExternalAssociation.Form.Component';

// functions
import * as ComponentFunctions from './PersonExternalAssociations.List.Functions';

const EditIcon: IIconProps = { iconName: 'Edit' };
const AddIcon: IIconProps = { iconName: 'Add' };

const PersonExternalAssociationsListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedAssociationId, setSelectedAssociationId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (props.PersonId) props.GetExternalAssociationsByPersonId(props.PersonId);
  }, [props.PersonId]);

  const handleOpenCreate = () => {
    setSelectedAssociationId(undefined);
    openForm();
  };

  const handleEdit = (associationId: number) => {
    setSelectedAssociationId(associationId);
    openForm();
  };

  const handleDismissForm = () => {
    setSelectedAssociationId(undefined);
    dismissForm();
  };

  return (
    <>
      <div className="ComponentHeaderControls">
        <PrimaryButton
          text={t('persons.externalAssociations.list.add', { defaultValue: 'Agregar asociación externa' })}
          iconProps={AddIcon}
          onClick={handleOpenCreate}
          className="actionButton"
        />
      </div>
      <Separator />

      {props.failOnLoading && (
        <MessageComponent
          message={t('persons.externalAssociations.list.error', {
            defaultValue: 'No fue posible obtener las asociaciones externas'
          })}
          subMessage={props.error?.ErrorMessage || ''}
          type={MessageBarType.error}
          itemList={props.error?.Errors || []}
        />
      )}

      {!props.isLoading && props.externalAssociations.length === 0 && (
        <MessageComponent
          message={t('persons.externalAssociations.list.empty', {
            defaultValue: 'No hay asociaciones externas registradas'
          })}
          type={MessageBarType.info}
        />
      )}

      {props.externalAssociations.length > 0 && (
        <table className="Table">
          <thead>
            <tr>
              <th>{t('persons.externalAssociations.list.organization', { defaultValue: 'Organización' })}</th>
              <th>{t('persons.externalAssociations.list.externalId', { defaultValue: 'Identificador externo' })}</th>
              <th>{t('persons.externalAssociations.list.externalType', { defaultValue: 'Tipo de entidad' })}</th>
              <th style={{ width: '140px' }}>
                {t('persons.common.actions', { defaultValue: 'Acciones' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {props.externalAssociations.map((item: ExternalEntityAssociationViewModel) => (
              <tr key={item.id} className="TableBodyRow">
                <td className="primary">
                  <Label className="upper">{item.externalEntityOrganization || '-'}</Label>
                </td>
                <td>
                  <Text>{item.externalEntityId || '-'}</Text>
                </td>
                <td>
                  <Text>{item.externalEntityType || '-'}</Text>
                </td>
                <td style={{ width: '140px' }}>
                  <IconButton
                    iconProps={EditIcon}
                    title={t('persons.common.edit', { defaultValue: 'Editar' })}
                    ariaLabel={t('persons.common.edit', { defaultValue: 'Editar' })}
                    onClick={() => handleEdit(item.id)}
                  />
                  <div className="danger">
                    <ConfirmationButtonComponent
                      ButtonId={`PersonExternalAssociationDeleteButton-${item.id}`}
                      ButtonIconName={'Delete'}
                      ButtonText={''}
                      ButtonCssClass={''}
                      ConfirmationButtonText={t('persons.common.delete', { defaultValue: 'Eliminar' })}
                      ConfirmationButtonCssClass={'deleteButton'}
                      CancelButtonText={t('persons.common.cancel', { defaultValue: 'Cancelar' })}
                      CancelButtonCssClass={''}
                      Title={t('persons.externalAssociations.list.deleteTitle', {
                        defaultValue: 'Eliminar asociación externa'
                      })}
                      Description={t('persons.externalAssociations.list.deleteDescription', {
                        defaultValue: '¿Desea eliminar la asociación externa seleccionada?'
                      })}
                      ElementTextHeader={t('persons.externalAssociations.list.organization', {
                        defaultValue: 'Organización'
                      })}
                      ElementTextInformation={item.externalEntityOrganization || ''}
                      onConfirmation={() => props.DeleteExternalAssociation(item.id)}
                      onCancel={() => undefined}
                      isOperationExecuting={props.isDeleting || false}
                      isOperationExecutedWithError={props.failOnDeleting || false}
                      OperationError={props.error}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {props.isLoading && (
        <div>
          <Spinner
            label={t('persons.externalAssociations.list.loading', {
              defaultValue: 'Cargando asociaciones externas...'
            })}
            ariaLive="assertive"
            labelPosition="top"
          />
        </div>
      )}

      <PersonExternalAssociationFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        PersonId={props.PersonId}
        externalAssociationId={selectedAssociationId}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PersonExternalAssociationsListComponent as any);
